/** Multilingual keywords & UI strings for SOC AI (Indian languages + English). */

export type LangKey =
  | 'en'
  | 'hi'
  | 'kn'
  | 'ta'
  | 'te'
  | 'mr'
  | 'bn'
  | 'gu'
  | 'ur'
  | 'ml'
  | 'pa'
  | 'or'
  | 'as';

export function langKeyFromCode(languageCode?: string): LangKey {
  const code = (languageCode ?? 'en-IN').split('-')[0].toLowerCase();
  const supported: LangKey[] = ['en', 'hi', 'kn', 'ta', 'te', 'mr', 'bn', 'gu', 'ur', 'ml', 'pa', 'or', 'as'];
  return (supported.includes(code as LangKey) ? code : 'en') as LangKey;
}

export function haystack(text: string): string {
  return text.normalize('NFC').toLocaleLowerCase('en-IN');
}

export function containsAny(text: string, needles: readonly string[]): boolean {
  const h = haystack(text);
  return needles.some((n) => h.includes(haystack(n)));
}

export function startsWithAny(text: string, needles: readonly string[]): boolean {
  const h = haystack(text.trim());
  return needles.some((n) => h.startsWith(haystack(n)));
}

export const PHRASES = {
  greeting: [
    'hi', 'hello', 'hey', 'namaste', 'namaskar', 'namaskara', 'namaskaram',
    'heli', 'helu', 'helide', 'halo', 'hallo', 'vanakkam', 'nomoshkar', 'pranam',
    'sat sri akal', 'kem cho', 'नमस्ते', 'नमस्कार', 'ಹಲೋ', 'ನಮಸ್ಕಾರ', 'வணக்கம்',
    'హలో', 'নমস্কার', 'નમસ્તે', 'سلام', 'നമസ്കാരം',
  ],
  thanks: [
    'thanks', 'thank you', 'bye', 'dhanyavad', 'dhanyavaad', 'shukriya',
    'nandi', 'nandri', 'aabhar', 'धन्यवाद', 'शुक्रिया', 'ಧನ್ಯವಾದ', 'நன்றி', 'ধন্যবাদ',
  ],
  reset: ['reset', 'start over', 'main menu', 'menu', 'never mind', 'hosa', 'phir se', 'dubara', 'ಮತ್ತೆ', 'மீண்டும்'],
  book: [
    'book', 'booking', 'schedule', 'book karo', 'book kari', 'book madi',
    'book cheyyi', 'book chey', 'book pannu', 'book pannunga', 'book karna',
    'book kar', 'service chahiye', 'service beku', 'service venam', 'service venum',
    'mujhe chahiye', 'nange beku', 'enakku venum', 'kaivangi', 'book madiri',
    'booking madi', 'service book', 'बुक करो', 'ಬುಕ್ ಮಾಡಿ', 'book cheyyandi',
  ],
  track: [
    'track', 'my orders', 'order status', 'booking status', 'where is my',
    'check order', 'show my bookings', 'order track', 'track karo', 'track madi',
    'order nodi', 'booking kaha', 'order kaha', 'status enu', 'status enti',
    'status enna', 'mera order', 'meri booking', 'order track karo',
    'ट्रैक', 'ಟ್ರ್ಯಾಕ್', 'ஆர்டர்', 'ఆర్డర్',
  ],
  cancel: [
    'cancel', 'cancellation', 'cancel karo', 'cancel madi', 'cancel cheyyi',
    'cancel pannu', 'raddu', 'raddu madi', 'cancel order', 'booking cancel',
    'order cancel karo', 'cancel my', 'रद्द', 'कैंसल', 'ರದ್ದು', 'ரத்து', 'రద్దు',
  ],
  price: [
    'price', 'pricing', 'cost', 'estimate', 'quote', 'charge', 'rate',
    'how much', 'kitna', 'kitne', 'eshtu', 'evlo', 'entha', 'evvalavu',
    'koto', 'daam', 'rate kya', 'price kya', 'belge eshtu',
    'कीमत', 'दाम', 'ಬೆಲೆ', 'விலை', 'ధర', 'कितना',
  ],
  payment: [
    'pay', 'payment', 'refund', 'upi', 'card', 'wallet', 'cash', 'paid',
    'complete payment', 'retry payment', 'bhugtan', 'pavati', 'pay karo',
    'पेमेंट', 'भुगतान', 'ಪಾವತಿ', 'பணம்', 'చెల్లింపు',
  ],
  addresses: [
    'address', 'addresses', 'my address', 'saved address', 'mera address',
    'nanna address', 'address nodi', 'पता', 'ವಿಳಾಸ', 'முகவரி', 'చిరునామా',
  ],
  services: [
    'show services', 'list services', 'available services', 'what services',
    'services available', 'service list', 'browse services', 'services torisi',
    'service torisi', 'sevegalu', 'services kya hai', 'सेवाएं', 'ಸೇವೆಗಳು',
  ],
  support: [
    'support', 'human', 'agent', 'email', 'contact', 'customer care',
    'help me', 'talk to support', 'madad', 'sahaya', 'सहायता', 'ಸಹಾಯ', 'உதவி',
  ],
  rebook: ['book again', 'same service', 'repeat booking', 'rebook', 'last booking', 'phir se book'],
  complaint: ['complaint', 'damage', 'late', 'not satisfied', 'poor service', 'issue', 'samasya'],
  yes: [
    'yes', 'y', 'yeah', 'confirm', 'ok', 'okay', 'sure', 'proceed',
    'haan', 'ha ji', 'ha', 'haudu', 'houdu', 'aam', 'seri', 'ama', 'avunu',
    'theek', 'thik', 'sahi', 'hou', 'हाँ', 'हां', 'ಹೌದು', 'ஆம்', 'అవును', 'होय',
  ],
  no: [
    'no', 'n', 'nope', 'stop', 'abort', 'nahi', 'na', 'illa', 'ledu',
    'illai', 'venda', 'radu', 'नहीं', 'ಇಲ್ಲ', 'இல்லை', 'కాదు', 'नाही',
  ],
  genericService: [
    'service', 'services', 'a service', 'service beku', 'service chahiye',
    'book a service', 'book service', 'seva book',
  ],
} as const;

export const UI: Record<LangKey, Record<string, string>> = {
  en: {
    welcomeTitle: 'Hello{name}. I am **SOC AI** for **Service On Click**.',
    welcomeBody:
      'Ask in **any language** — book, track, cancel, price, payment, FAQ:\n' +
      '• *book 1bhk cleaning tomorrow*\n• *where is my order*\n• *cancel last booking*',
    signInBook: 'Please sign in to book a service.',
    signInTrack: 'Please sign in to check your bookings.',
    paymentChoose: 'Choose payment method:',
    payOnline: 'Pay online',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Go to payment',
    payNow: 'Pay now',
    bookingReady: 'I prepared your booking:',
    fallback:
      'I am **SOC AI**. Ask in **English, Hindi, Kannada, Tamil, Telugu**, or any language — book, track, cancel, price, payment, or FAQ.',
  },
  hi: {
    welcomeTitle: 'Namaste{name}! Main **SOC AI** hoon.',
    welcomeBody:
      'Kisi bhi language mein poochhein — **book**, **track**, **cancel**, **price**:\n' +
      '• *1bhk cleaning book karo*\n• *mera order kaha hai*\n• *cancel karo*',
    signInBook: 'Booking ke liye sign in karein.',
    signInTrack: 'Bookings dekhne ke liye sign in karein.',
    paymentChoose: 'Payment method chunein:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment par jayein',
    payNow: 'Ab pay karein',
    bookingReady: 'Maine aapki booking tayyar kar di:',
    fallback: 'Main **SOC AI** hoon. Hindi, English, Kannada — kisi bhi language mein poochhein.',
  },
  kn: {
    welcomeTitle: 'ನಮಸ್ಕಾರ{name}! ನಾನು **SOC AI**.',
    welcomeBody:
      'ಯಾವ ಭಾಷೆಯಲ್ಲಾದರೂ ಕೇಳಿ — **book**, **track**, **cancel**, **price**:\n' +
      '• *1bhk cleaning book madi*\n• *nanna order elli*\n• *cancel madi*',
    signInBook: 'Booking ge sign in madi.',
    signInTrack: 'Bookings nodalu sign in madi.',
    paymentChoose: 'Payment method choose madi:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment ge hogi',
    payNow: 'Iga pay madi',
    bookingReady: 'Nimma booking ready:',
    fallback: 'Naan **SOC AI**. Kannada, Hindi, English — yav bhasheliyalli keli.',
  },
  ta: {
    welcomeTitle: 'Vanakkam{name}! Naan **SOC AI**.',
    welcomeBody: 'Etha language laalum kelunga — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking ku sign in pannunga.',
    signInTrack: 'Bookings paarka sign in pannunga.',
    paymentChoose: 'Payment method select pannunga:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment ku ponga',
    payNow: 'Ippovae pay pannunga',
    bookingReady: 'Unga booking ready:',
    fallback: 'Naan **SOC AI**. Tamil, English, Hindi — etha mozhiyilum kelunga.',
  },
  te: {
    welcomeTitle: 'Namaskaram{name}! Nenu **SOC AI**.',
    welcomeBody: 'Ee bhashalo adagandi — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking ki sign in avandi.',
    signInTrack: 'Bookings chudataniki sign in avandi.',
    paymentChoose: 'Payment method emi pick chestaru:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment ki vellandi',
    payNow: 'Ippudu pay cheyyandi',
    bookingReady: 'Mee booking ready:',
    fallback: 'Nenu **SOC AI**. Telugu, Hindi, English — ye bhashalo adagandi.',
  },
  mr: {
    welcomeTitle: 'Namaskar{name}! Mi **SOC AI** aahe.',
    welcomeBody: 'Konatyahi bhashet vicharā — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking sathi sign in kara.',
    signInTrack: 'Bookings pahanyasathi sign in kara.',
    paymentChoose: 'Payment method nivda:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment la jaa',
    payNow: 'Ata pay kara',
    bookingReady: 'Tumchi booking tayar:',
    fallback: 'Mi **SOC AI**. Marathi, Hindi, English — vicharā.',
  },
  bn: {
    welcomeTitle: 'Nomoshkar{name}! Ami **SOC AI**.',
    welcomeBody: 'Je kono bhasay jigges kora — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking er jonno sign in korun.',
    signInTrack: 'Bookings dekhte sign in korun.',
    paymentChoose: 'Payment method nirbachan korun:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment e jan',
    payNow: 'Ekhon pay korun',
    bookingReady: 'Apnar booking ready:',
    fallback: 'Ami **SOC AI**. Bangla, Hindi, English — jigges korun.',
  },
  gu: {
    welcomeTitle: 'Namaste{name}! Hu **SOC AI** chu.',
    welcomeBody: 'Koi pan bhasa ma puchho — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking mate sign in karo.',
    signInTrack: 'Bookings jova sign in karo.',
    paymentChoose: 'Payment method pasand karo:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment par jao',
    payNow: 'Have pay karo',
    bookingReady: 'Tamari booking ready:',
    fallback: 'Hu **SOC AI**. Gujarati, Hindi, English — puchho.',
  },
  ur: {
    welcomeTitle: 'Assalam{name}! Main **SOC AI** hoon.',
    welcomeBody: 'Kisi bhi zaban mein poochein — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking ke liye sign in karein.',
    signInTrack: 'Bookings dekhne ke liye sign in karein.',
    paymentChoose: 'Payment method chunein:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment par jayein',
    payNow: 'Ab pay karein',
    bookingReady: 'Aapki booking tayyar:',
    fallback: 'Main **SOC AI**. Urdu, Hindi, English — poochein.',
  },
  ml: {
    welcomeTitle: 'Namaskaram{name}! Njan **SOC AI**.',
    welcomeBody: 'Etha bhashayilum chodikkuka — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking nu sign in cheyyuka.',
    signInTrack: 'Bookings kanan sign in cheyyuka.',
    paymentChoose: 'Payment method therinjedukkuka:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment lekku pokuka',
    payNow: 'Ippol pay cheyyuka',
    bookingReady: 'Ningalude booking ready:',
    fallback: 'Njan **SOC AI**. Malayalam, Hindi, English — chodikkuka.',
  },
  pa: {
    welcomeTitle: 'Sat Sri Akal{name}! Main **SOC AI** haan.',
    welcomeBody: 'Kise vi bhasha vich puchho — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking layi sign in karo.',
    signInTrack: 'Bookings vekh layi sign in karo.',
    paymentChoose: 'Payment method chuno:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment te jao',
    payNow: 'Hun pay karo',
    bookingReady: 'Tuhadi booking ready:',
    fallback: 'Main **SOC AI**. Punjabi, Hindi, English — puchho.',
  },
  or: {
    welcomeTitle: 'Namaskar{name}! Mu **SOC AI**.',
    welcomeBody: 'Kono bhasare puchhantu — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking pain sign in karantu.',
    signInTrack: 'Bookings dekhibaku sign in karantu.',
    paymentChoose: 'Payment method chunantu:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment ku jantu',
    payNow: 'Ebe pay karantu',
    bookingReady: 'Apananka booking ready:',
    fallback: 'Mu **SOC AI**. Odia, Hindi, English — puchhantu.',
  },
  as: {
    welcomeTitle: 'Nomoskar{name}! Moi **SOC AI**.',
    welcomeBody: 'Konobad language t puchok — **book**, **track**, **cancel**, **price**.',
    signInBook: 'Booking r karone sign in krok.',
    signInTrack: 'Bookings sabole sign in krok.',
    paymentChoose: 'Payment method bachok:',
    payOnline: 'Online pay',
    partialPay: 'Partial pay',
    cashOnService: 'Cash on service',
    goToPayment: 'Payment lo jao',
    payNow: 'Etya pay krok',
    bookingReady: 'Apunar booking ready:',
    fallback: 'Moi **SOC AI**. Assamese, Hindi, English — puchok.',
  },
};

export function t(lang: LangKey, key: string, vars?: Record<string, string>): string {
  const str = UI[lang]?.[key] ?? UI.en[key] ?? key;
  if (!vars) return str;
  return Object.entries(vars).reduce((s, [k, v]) => s.replace(`{${k}}`, v), str);
}

export function isYes(text: string): boolean {
  return startsWithAny(text, PHRASES.yes);
}

export function isNo(text: string): boolean {
  return startsWithAny(text, PHRASES.no);
}

export function isGenericServicePhrase(text: string): boolean {
  return containsAny(text, PHRASES.genericService);
}
